import { BaseAdaptiveCardRenderer } from '@ninjacart/nc-nk-adaptive-cards';
import { IActionDataModel, IAdaptiveActionCard, IEdTechContentModel } from '@ninjacart/nc-nk-core';
import { addEventParam, INinjaEvent, useQueuedEventStore } from '@ninjacart/nc-events-sdk-v2';
import {
  KissanEventType,
  truncateName,
  useEdTechProductStore,
  useProfileStore,
} from '@ninjacart/nc-nk-store';
import { View, XStack } from '@ninjacart/nc-nk-ui';
import { Footer, useFocusEffect, useRouter } from '@ninjacart/nc-nk-web-deps';
import { FlashList } from '@shopify/flash-list';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, Platform, Pressable, ViewToken } from 'react-native';
import { getProductList } from '../../api/kisan/getProductList';
import { ProductListPlaceHolder } from '../edTechPlaceHolder/productListPlaceHolder';
import { ProductEmptyStateComponent } from '../emptyState/productEmptyStateComponent';
import EdTechHomeHeader from '../header/edTechHeader';
import { EdTechSearchComponent } from '../search/edTeachSearch';

export function EdTechProductList() {
  const { t } = useTranslation();
  const userProfile = useProfileStore((state) => state?.userProfile);
  const { push } = useRouter();
  const [productListData, setProductListData] = useState<
    IAdaptiveActionCard<IActionDataModel<IEdTechContentModel>>[]
  >([]);
  const startCountRef = useRef({ start: 0, count: 10 });
  const [isLoaded, setIsLoaded] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState(false);
  const deviceWidth = Dimensions.get('window').width;
  const enqueue = useRef(useQueuedEventStore((state) => state.enqueue)).current;
  const viewabilityConfig = useRef({
    // waitForInteraction: true,
    itemVisiblePercentThreshold: 80,
    minimumViewTime: 1000,
  }).current;
  const [visibleItemIndex, setVisibleItemIndex] = useState<number | null>(null);
  const setScreenName = useEdTechProductStore((state) => state.setScreenName);
  const setProductSearchType = useEdTechProductStore((state) => state.setProductSearchType);
  const hasScrolled = useRef(false);

  const init = async () => {
    setIsLoaded(true);
    let payload = {
      geoLatitude: '',
      geoLongitude: '',
      offset: startCountRef?.current?.start,
      count: startCountRef?.current?.count,
    };
    try {
      const data = await getProductList(payload);
      if (data?.data?.length) {
        setProductListData(data?.data);
      }
    } catch (error) {
      console.debug('product list error', error);
    } finally {
      setIsLoaded(false);
      setRefreshing(false);
    }
  };

  useFocusEffect(
    useCallback(() => {
      setScreenName('product list page');
      init();
    }, [])
  );

  const renderItem = ({ item, index }: { item: any; index: number }) => {
    const commonProps = {
      item,
      index,
      deviceWidth,
      t,
      carouselData: item?.actionData,
      name: item.type,
      sourcePage: 'product',
    };
    return (
      <View flex={1}>
        <BaseAdaptiveCardRenderer {...commonProps} />
      </View>
    );
  };

  const handleRedirection = () => {
    setProductSearchType('');
    handleProductSearchEvent();
    push({
      pathname: `/edTech/product/productSearch`,
    });
  };

  const handleProductSearchEvent = () => {
    const productSearchClickEvent: INinjaEvent = {
      name: KissanEventType.PRODUCT_SEARCH_CLICK,
      screenDetails: { screen: 'machinery list' },
      eventParams: [addEventParam('productCategory', 'product')],
    };

    enqueue(productSearchClickEvent);
  };

  const listHeaderComponent = () => {
    return (
      <>
        <Pressable onPress={handleRedirection}>
          <XStack marginTop={10} paddingHorizontal={10} testID="kisan_tId_productSearch">
            <EdTechSearchComponent />
          </XStack>
        </Pressable>
      </>
    );
  };

  const listEmptyComponent = () => {
    return <ProductEmptyStateComponent />;
  };

  const onPullToRefresh = async () => {
    console.log('On refresh called!!!');
    setRefreshing(true);
    setIsLoaded(true);
  };

  const captureImpressionEvent = (index: number, itemId: number | undefined, type: string) => {
    const additionalDetails = [
      addEventParam('scrolledIndex', index),
      addEventParam('itemId', JSON.stringify(itemId)),
      addEventParam('type', type),
    ];

    const dataViewedMoreThanOneSecEvent: INinjaEvent = {
      name: KissanEventType.DATA_VIEWED_MORETHAN_ONE_SECOND,
      screenDetails: { screen: 'product list page' },
      eventParams: additionalDetails,
    };
    enqueue(dataViewedMoreThanOneSecEvent);
    console.log(`Impression event for item at index ${index} in Product List page`);
  };

  const viewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      if (viewableItems?.length > 1) {
        viewableItems.forEach((item) => {
          const index = item.index;
          const type = item?.item?.type;
          const productIds = Array.isArray(item?.item?.actionData)
            ? item.item.actionData
                .slice(0, 6)
                .filter((action) => action?.content?.productId)
                .map((action) => action?.content?.productId)
            : [];

          if (typeof index === 'number') {
            setVisibleItemIndex(index);
            captureImpressionEvent(index, productIds, type);
          }
        });
      }
    },
    [visibleItemIndex, userProfile]
  );

  useEffect(() => {
    if (refreshing) {
      startCountRef.current.start = 0;
      init();
    }
  }, [refreshing]);

  if (isLoaded) {
    return <ProductListPlaceHolder />;
  }

  const handleOnScroll = () => {
    if (!hasScrolled.current) {
      hasScrolled.current = true;
      const productViewAllEvent: INinjaEvent = {
        name: KissanEventType.PRODUCT_PAGE_SCROLL,
        screenDetails: { screen: 'product list page' },
      };
      enqueue(productViewAllEvent);
    }
  };

  return (
    <View flex={1} backgroundColor="$cardbackground">
      <EdTechHomeHeader
        title={`${t('page.namaste')} ${truncateName(userProfile?.user?.name ?? '', 15)}`}
        kisanLogoShow={true}
        applyMargin={true}
        showMyOrder={true}
      />

      <FlashList
        data={productListData}
        renderItem={renderItem}
        getItemType={(item) => item.type}
        keyExtractor={(item, index) => {
          return index.toString();
        }}
        scrollEventThrottle={24}
        estimatedItemSize={220}
        onScroll={handleOnScroll}
        showsVerticalScrollIndicator={false}
        viewabilityConfig={viewabilityConfig}
        onViewableItemsChanged={viewableItemsChanged}
        onEndReachedThreshold={0.7}
        ListHeaderComponent={listHeaderComponent()}
        ListEmptyComponent={listEmptyComponent()}
        onRefresh={onPullToRefresh}
        refreshing={refreshing}
        ListFooterComponent={() => {
          return Platform.OS === 'web' ? <Footer sourcepage={'product list page'} /> : null;
        }}
      ></FlashList>
    </View>
  );
}
